import api from '../../Components/RefreshToken/RefreshToken';
import { cached } from './cache';

// Kabinetlar uchun haqiqiy ma'lumot: FAQAT GET so'rovlar, mavjud `api` instance va Authorization bilan.
// Ishlatiladigan endpoint'lar ro'yxati docs/roles.md da. Javoblar sessiya davomida keshlanadi.

const authHeaders = () => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
});

const get = (url, params) => api.get(url, { headers: authHeaders(), params }).then((response) => response.data);

// GET /management/staff/profile → { id, first_name, last_name, phone_number, role } (keshlanmaydi: demo rolni aniqlash uchun)
export const getStaffProfile = () => get('/management/staff/profile');

const PAGE_SIZE = 100;
const MAX_PAGES = 50;

// total maydoni bo'lgan ro'yxatlarni oxirigacha o'qish
async function fetchAllPaged(loadPage, pickItems, pickTotal) {
  const items = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = await loadPage(page * PAGE_SIZE, PAGE_SIZE);
    const chunk = pickItems(data) || [];
    items.push(...chunk);
    const total = pickTotal ? pickTotal(data) : null;
    if (chunk.length < PAGE_SIZE || (total !== null && items.length >= total)) break;
  }
  return items;
}

// GET /management/dashboard/statistics/head
export const getHeadStatistics = () =>
  cached('statistics/head', () => get('/management/dashboard/statistics/head'));

// GET /management/students-dinamics-weekly → [{ day_name, count }]
export const getWeeklyDynamics = () =>
  cached('students-dinamics-weekly', () => get('/management/students-dinamics-weekly'));

// GET /management/students?skip&limit&search&course_id&from_created_at&to_created_at
export function getStudentsPage({ skip = 0, limit = 20, search, courseId, from, to } = {}) {
  const params = { skip, limit };
  if (search) params.search = search;
  if (courseId) params.course_id = courseId;
  if (from) params.from_created_at = from;
  if (to) params.to_created_at = to;
  return cached(`students:${JSON.stringify(params)}`, () => get('/management/students', params));
}

export const getAllStudents = () =>
  cached('students:all', () =>
    fetchAllPaged(
      (skip, limit) => get('/management/students', { skip, limit }),
      (data) => data?.result,
      (data) => (typeof data?.total_count === 'number' ? data.total_count : null)
    )
  );

// GET /courses/courses/admin?active
export const getCoursesAdmin = (active = true) =>
  cached(`courses/admin:${active}`, () => get('/courses/courses/admin', { skip: 0, limit: 1000, active }));

// GET /courses/stat → [{ id, title, number_of_students, number_of_chapters, completed, not_completed }]
export const getCourseStats = () => cached('courses/stat', () => get('/courses/stat'));

// GET /courses/detail/{id} → { translations, chapters: [{ id, translations }] }
export const getCourseDetail = (courseId) => cached(`courses/detail:${courseId}`, () => get(`/courses/detail/${courseId}`));

// GET /courses/chapters?course_id= → [{ id, title, order }]
export const getChapters = (courseId) => cached(`chapters:${courseId}`, () => get('/courses/chapters', { course_id: courseId }));

// GET /courses/chapters/{id}/lessons → [{ id, title, description, order }]
export const getLessons = (chapterId) => cached(`lessons:${chapterId}`, () => get(`/courses/chapters/${chapterId}/lessons`));

// GET /management/sales/report → { total_orders, paid_orders, promo_orders, total_revenue }
export const getSalesReport = () => cached('sales/report', () => get('/management/sales/report'));

// GET /management/sales/orders?skip&limit → { total_count, result: [{ id, user, course, paid_amount, promo_code, created_at }] }
export const getOrdersPage = ({ skip = 0, limit = 20 } = {}) =>
  cached(`orders:${skip}:${limit}`, () => get('/management/sales/orders', { skip, limit }));

export const getAllOrders = () =>
  cached('orders:all', () =>
    fetchAllPaged(
      (skip, limit) => get('/management/sales/orders', { skip, limit }),
      (data) => data?.result,
      (data) => (typeof data?.total_count === 'number' ? data.total_count : null)
    )
  );

// GET /management/exam/results?skip&limit → [{ learner_id, chapter_id, total_score, passed, ... }] (sana va kurs yo'q)
export const getAllExamResults = () =>
  cached('exam/results:all', () =>
    fetchAllPaged((skip, limit) => get('/management/exam/results', { skip, limit }), (data) => (Array.isArray(data) ? data : []))
  );

// GET /management/students/{sid}/{cid}/examed/chapters
export const getExamedChapters = (studentId, courseId) =>
  cached(`examed:${studentId}:${courseId}`, () => get(`/management/students/${studentId}/${courseId}/examed/chapters`));

// GET /management/students/{sid}/{cid}/{chapter_id}/exam-results
export const getChapterExamResults = (studentId, courseId, chapterId) =>
  cached(`exam-results:${studentId}:${courseId}:${chapterId}`, () =>
    get(`/management/students/${studentId}/${courseId}/${chapterId}/exam-results`)
  );

// GET /certificates/?skip&limit → { total, result: [{ id, user, course, created_at }] }
export const getAllCertificates = () =>
  cached('certificates:all', () =>
    fetchAllPaged(
      (skip, limit) => get('/certificates/', { skip, limit }),
      (data) => data?.result,
      (data) => (typeof data?.total === 'number' ? data.total : null)
    )
  );

// GET /promo/list → [{ id, promo_type, codes_count, courses, is_active, valid_from, valid_to, ... }]
export const getPromoGroups = () => cached('promo/list', () => get('/promo/list'));

// GET /promo/{id}/codes → [{ id, code, usage_limit, used_count }]
export const getPromoCodes = (groupId) => cached(`promo/codes:${groupId}`, () => get(`/promo/${groupId}/codes`));

// GET /management/student/complaints → [{ id, title, description, user, course, mentor, date_time, reviewed }]
export const getStudentComplaints = () => cached('student/complaints', () => get('/management/student/complaints'));

// GET /management/mentors → [{ id, first_name, last_name }]
export const getMentors = () => cached('mentors', () => get('/management/mentors'));

// Bob → kurs xaritasi: har kurs uchun /courses/chapters (keshlangan). { [chapterId]: courseId }
export const getChapterCourseMap = () =>
  cached('chapter-course-map', async () => {
    const courses = await getCourseStats();
    const entries = await Promise.all(
      (courses || []).map((course) =>
        getChapters(course.id)
          .then((chapters) => (chapters || []).map((chapter) => [chapter.id, course.id]))
          .catch(() => [])
      )
    );
    return Object.fromEntries(entries.flat());
  });

// Bob indeksi: { [chapterId]: { courseId, courseTitle, title, order } } (getChapters keshidan foydalanadi)
export const getChapterIndex = () =>
  cached('chapter-index', async () => {
    const courses = await getCourseStats();
    const entries = await Promise.all(
      (courses || []).map((course) =>
        getChapters(course.id)
          .then((chapters) =>
            (chapters || []).map((chapter) => [chapter.id, { courseId: course.id, courseTitle: course.title, title: chapter.title, order: chapter.order }])
          )
          .catch(() => [])
      )
    );
    return Object.fromEntries(entries.flat());
  });

// Kurs pasporti: boblar va har bobdagi darslar soni: [{ chapter, lessons: number | null, error? }]
export const getCourseOutline = (courseId) =>
  cached(`course-outline:${courseId}`, async () => {
    const chapters = await getChapters(courseId);
    return Promise.all(
      (chapters || []).map((chapter) =>
        getLessons(chapter.id)
          .then((lessons) => ({ chapter, lessons: (lessons || []).length }))
          .catch((error) => ({ chapter, lessons: null, error }))
      )
    );
  });

// Barcha kurslar tafsiloti (tarjimalar mavjudligi uchun): [{ stat, detail | null, error? }]
export const getAllCourseDetails = () =>
  cached('courses/detail:all', async () => {
    const courses = await getCourseStats();
    return Promise.all(
      (courses || []).map((course) =>
        getCourseDetail(course.id)
          .then((detail) => ({ stat: course, detail }))
          .catch((error) => ({ stat: course, detail: null, error }))
      )
    );
  });
